import { debounce } from 'lodash-es';
import { computed, ref, watch } from 'vue';
import {
  useResourceEventsAllQuery,
  useResourceEventsUserQuery,
} from '/@entities/resourceEvent/api/useResourceEventsQueries';
import { loadResourceFilter } from '/@stores/resources';
import { search } from '/@utilities/general';

async function getUsersByGroup(groupId: number): Promise<Array<string>> {
  const filter = await loadResourceFilter();
  if (!filter) return [];
  return filter
    .filter((group) => group.id === groupId)
    .flatMap((group) => group.resources.map((resource) => resource.guid));
}

export function useResourceEvents({ byUser = false }: { byUser?: boolean } = {}) {
  const selectedDate = ref<Date | null>(null);
  const dateRange = ref<{ start: Date; end: Date } | null>(null);
  const userSearch = ref<string | null>(null);
  const groupSearch = ref<number | null>(null);
  const selectedGroupUsers = ref<Array<string>>([]);

  watch(groupSearch, async (newGroupId) => {
    selectedGroupUsers.value = newGroupId ? await getUsersByGroup(newGroupId) : [];
  });

  const { data, isLoading } = byUser
    ? useResourceEventsUserQuery(dateRange)
    : useResourceEventsAllQuery(dateRange);

  const events = computed(() =>
    (data.value ?? []).filter((event) => {
      if (event.resourceType === 'group') return false;
      if (userSearch.value && !search(userSearch.value, event.userName)) return false;
      if (
        selectedGroupUsers.value.length > 0 &&
        !selectedGroupUsers.value.includes(event.resourceGuid)
      )
        return false;
      return true;
    }),
  );

  const dateChange = debounce(({ start, end }: { start: Date; end: Date }) => {
    selectedDate.value = start;
    dateRange.value = { start, end };
  }, 300);

  return {
    isLoading,
    selectedDate,
    events,
    groupSearch,
    selectedGroupUsers,
    userSearch,
    dateChange,
  };
}
